import { CaretDown, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const FEED_PARTNER_OPTIONS = [
  { value: "all", label: "All feed partners" },
  { value: "impact", label: "Impact" },
  { value: "skimlinks", label: "Skimlinks" },
  { value: "mrge", label: "Mrge" },
  { value: "partnerize", label: "Partnerize" },
  { value: "yieldkit", label: "Yieldkit" },
  { value: "shopnomix", label: "Shopnomix" },
  { value: "cj", label: "CJ" },
  { value: "admedia", label: "AdMedia" },
  { value: "pubadhub", label: "PubAdHub" },
  { value: "pubadhub_kevin", label: "PubAdHub 2" },
  { value: "flexoffers", label: "FlexOffers" },
  { value: "shopnomix_kevin", label: "Shopnomix InMobi" },
  { value: "siteplug", label: "Siteplug" },
  { value: "giddyup", label: "GiddyUp" },
  { value: "pronomix", label: "Pronomix" },
  { value: "maxbounty", label: "MaxBounty" },
  { value: "cashbackhive", label: "CashbackHive" },
];

const PARTNER_OPTIONS = FEED_PARTNER_OPTIONS.filter((o) => o.value !== "all");

export function parseNetworkFilter(value) {
  if (!value || value === "all") return [];
  if (Array.isArray(value)) {
    return [...new Set(value.map((v) => String(v || "").trim().toLowerCase()).filter((v) => v && v !== "all"))];
  }
  return [...new Set(
    String(value)
      .split(",")
      .map((v) => v.trim().toLowerCase())
      .filter((v) => v && v !== "all"),
  )];
}

export function serializeNetworkFilter(selected) {
  const uniq = parseNetworkFilter(Array.isArray(selected) ? selected.join(",") : selected);
  return uniq.length ? uniq.join(",") : "all";
}

export function appendNetworkParam(params, network, discloseNetworks = true) {
  if (!discloseNetworks) return;
  const parsed = parseNetworkFilter(network);
  if (parsed.length) params.set("network", parsed.join(","));
}

export function appendRouteFromParam(params, routeFrom) {
  const parsed = parseNetworkFilter(routeFrom);
  if (parsed.length) params.set("route_from", parsed.join(","));
}

function mergeOptions(extraOptions = []) {
  const seen = new Set(PARTNER_OPTIONS.map((o) => o.value));
  const extras = [];
  for (const raw of extraOptions) {
    const value = String(raw?.value || raw || "").trim().toLowerCase();
    if (!value || value === "all" || seen.has(value)) continue;
    seen.add(value);
    extras.push({ value, label: raw?.label || value });
  }
  return [...PARTNER_OPTIONS, ...extras];
}

export function triggerLabel(selected, options, allLabel, summarizeExcept = false) {
  if (!selected.length) return allLabel;
  const missing = options.filter((o) => !selected.includes(o.value));
  if (summarizeExcept && missing.length === 1 && selected.length > 1) return `All except ${missing[0].label}`;
  return selected
    .map((v) => options.find((o) => o.value === v)?.label || v)
    .join(", ");
}

function VisibleCheckbox({ checked }) {
  return (
    <span
      aria-hidden="true"
      className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border ${
        checked
          ? "border-[#002FA7] bg-[#002FA7] text-white"
          : "border-slate-400 bg-white"
      }`}
    >
      {checked ? <Check size={11} weight="bold" /> : null}
    </span>
  );
}

export default function FeedPartnerSelect({
  value,
  onChange,
  testId = "filter-network",
  extraOptions = [],
  allLabel = "All feed partners",
  menuLabel = "Feed partners",
  summarizeExcept = false,
  className = "min-w-[200px] max-w-[360px]",
}) {
  const options = mergeOptions(extraOptions);
  const selected = parseNetworkFilter(value);
  const allChecked = selected.length === 0;

  const emit = (next) => {
    const uniq = [...new Set(next)];
    // Checking every partner is the same as no filter.
    if (uniq.length === 0 || uniq.length >= options.length) {
      onChange?.("all");
      return;
    }
    onChange?.(uniq.join(","));
  };

  const toggle = (networkValue) => {
    if (selected.includes(networkValue)) {
      emit(selected.filter((v) => v !== networkValue));
    } else {
      emit([...selected, networkValue]);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`h-9 justify-between font-normal ${className}`}
          data-testid={testId}
          title={triggerLabel(selected, options, allLabel, summarizeExcept)}
        >
          <span className="min-w-0 truncate">{triggerLabel(selected, options, allLabel, summarizeExcept)}</span>
          <CaretDown size={12} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto" data-testid={`${testId}-menu`}>
        <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={(e) => {
            e.preventDefault();
            onChange?.("all");
          }}
          data-testid={`${testId}-all`}
        >
          <VisibleCheckbox checked={allChecked} />
          {allLabel}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {options.map((opt) => {
          const checked = selected.includes(opt.value);
          return (
            <DropdownMenuItem
              key={opt.value}
              className="cursor-pointer"
              onSelect={(e) => {
                e.preventDefault();
                toggle(opt.value);
              }}
              data-testid={`${testId}-${opt.value}`}
            >
              <VisibleCheckbox checked={checked} />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
